import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import { isDemoMode } from './lib/demoMode';
import { ThemeProvider, useTheme } from './lib/ThemeContext';
import { OnboardingProvider } from './components/OnboardingTour';
import { PlanProvider } from './lib/PlanContext';
import Layout from './components/Layout';
import Auth from './pages/Auth';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Projects from './pages/Projects';
import Invoices from './pages/Invoices';
import Analytics from './pages/Analytics';
import AiAdvice from './pages/AiAdvice';
import Profile from './pages/Profile';
import Expenses from './pages/Expenses';
import WorkProof from './pages/WorkProof';
import Legal from './pages/Legal';

// 未ログイン時の法務ページ（Layoutなしの素の画面）
// 特商法・利用規約・プラポリは、登録前の人とStripe審査担当が見られる必要がある
function LegalStandalone({ doc }) {
  const { theme } = useTheme();
  return (
    <div style={{ minHeight: '100vh', background: theme.bg, padding: '32px 16px 64px' }}>
      <Legal doc={doc} />
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  useEffect(() => {
    // ① URLハッシュを先にチェック（getSessionより前に実行）
    if (window.location.hash.includes('type=recovery')) {
      setIsPasswordRecovery(true);
    }

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      // ② PASSWORD_RECOVERY イベントで確実に検知
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
        setSession(s);
        return;
      }
      // ③ updateUser完了後はフラグをリセット
      if (event === 'USER_UPDATED') {
        setIsPasswordRecovery(false);
      }
      setSession(s);
    });

    return () => subscription.unsubscribe();
  }, []); // supabaseクライアントとsetterは安定した参照のためdepsから除外

  if (loading) return (
    <ThemeProvider>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0f172a' }}>
        <div style={{ width: 32, height: 32, border: '3px solid #6366f1', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    </ThemeProvider>
  );

  // パスワードリセットリンクから来た場合 → セッションがあっても更新画面を強制表示
  if (isPasswordRecovery) return (
    <ThemeProvider>
      <Router>
        <Routes>
          <Route path="/legal/terms"     element={<LegalStandalone doc="terms" />} />
          <Route path="/legal/privacy"   element={<LegalStandalone doc="privacy" />} />
          <Route path="/legal/tokushoho" element={<LegalStandalone doc="tokushoho" />} />
          <Route path="*" element={
            <Auth
              initialMode="update_password"
              onPasswordUpdated={() => setIsPasswordRecovery(false)}
            />
          } />
        </Routes>
      </Router>
    </ThemeProvider>
  );

  // 未ログイン → ログイン画面。ただし法務3ページだけは開ける
  if (!session && !isDemoMode()) return (
    <ThemeProvider>
      <Router>
        <Routes>
          <Route path="/legal/terms"     element={<LegalStandalone doc="terms" />} />
          <Route path="/legal/privacy"   element={<LegalStandalone doc="privacy" />} />
          <Route path="/legal/tokushoho" element={<LegalStandalone doc="tokushoho" />} />
          <Route path="*" element={<Auth />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );

  return (
    <ThemeProvider>
      <OnboardingProvider>
        <PlanProvider>
          <Router>
            <Layout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/clients" element={<Clients />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/invoices" element={<Invoices />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/expenses" element={<Expenses />} />
                <Route path="/ai" element={<AiAdvice />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/work-proof" element={<WorkProof />} />
                <Route path="/legal/terms" element={<Legal doc="terms" />} />
                <Route path="/legal/privacy" element={<Legal doc="privacy" />} />
                <Route path="/legal/tokushoho" element={<Legal doc="tokushoho" />} />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </Layout>
          </Router>
        </PlanProvider>
      </OnboardingProvider>
    </ThemeProvider>
  );
}
